import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import type { DrawerProps } from "../types/components.js";

const sizeClasses = {
  default: "max-w-sm",
  wide: "max-w-md",
} as const;

const EXIT_MS = 300;

export function Drawer({
  open,
  title,
  controlId,
  closeLabel,
  children,
  onClose,
  side = "right",
  size = "default",
  description,
  icon,
  bodyClassName = "",
}: DrawerProps) {
  const closeButton = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);
  const titleId = `${controlId}-title`;
  const descriptionId = `${controlId}-description`;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (open) {
      setMounted(true);
      setEntered(false);
      let innerFrame = 0;
      const outerFrame = window.requestAnimationFrame(() => {
        // Second frame so the closed transform is painted before we animate in.
        innerFrame = window.requestAnimationFrame(() => setEntered(true));
      });
      return () => {
        window.cancelAnimationFrame(outerFrame);
        window.cancelAnimationFrame(innerFrame);
      };
    }

    setEntered(false);
    const timer = window.setTimeout(() => setMounted(false), EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open || !mounted) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [mounted, open]);

  // Refocus when the drawer stays open but panel content/title changes (e.g. learn → ask).
  useEffect(() => {
    if (!open || !mounted) return;
    closeButton.current?.focus();
  }, [mounted, open, title]);

  if (!mounted) return null;

  const closedTransform = side === "right" ? "translate-x-full" : "-translate-x-full";

  return (
    <div className="fixed inset-0 z-50" role="presentation">
      <div
        aria-hidden="true"
        className={`absolute inset-0 bg-foreground/20 backdrop-blur-sm transition-opacity duration-300 ease-out motion-reduce:transition-none ${
          entered ? "opacity-100" : "opacity-0"
        }`}
        data-control-id={`${controlId}.backdrop`}
        onClick={() => onCloseRef.current()}
      />
      <section
        aria-describedby={description ? descriptionId : undefined}
        aria-labelledby={titleId}
        aria-modal="true"
        className={`absolute inset-y-0 flex w-full flex-col border-border bg-popover text-popover-foreground shadow-2xl transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transform-none motion-reduce:transition-none ${sizeClasses[size]} ${
          side === "right" ? "right-0 border-l" : "left-0 border-r"
        } ${entered ? "translate-x-0" : closedTransform}`}
        data-control-id={controlId}
        role="dialog"
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border/70 px-5 py-4 sm:px-6">
          <div className="flex min-w-0 items-start gap-3">
            {icon}
            <div className="min-w-0">
              <h2 className="text-lg font-medium leading-snug" id={titleId}>
                {title}
              </h2>
              {description ? (
                <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground" id={descriptionId}>
                  {description}
                </p>
              ) : null}
            </div>
          </div>
          <button
            aria-label={closeLabel}
            className="grid size-9 shrink-0 place-items-center rounded-full bg-muted text-foreground transition-colors hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
            data-control-id={`${controlId}.close`}
            onClick={() => onCloseRef.current()}
            ref={closeButton}
            type="button"
          >
            <X aria-hidden="true" className="size-4" strokeWidth={2.2} />
          </button>
        </header>
        <div
          className={
            bodyClassName
              ? `min-h-0 flex-1 ${bodyClassName}`
              : "min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6"
          }
        >
          {children}
        </div>
      </section>
    </div>
  );
}
